import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, of } from 'rxjs';
import { AuthService } from '../../services/auth/auth.service';
import { DashboardService } from '../../services/dashboard.service';
import { FormatPipe } from '../../utils/format.pipe';
import { SUPPORT_WHATSAPP_NUMBER } from '../../utils/whatsapp-number';

const CONFIRMATION_TIMEOUT_MS = 6000;

interface FeedbackPayload {
  rating: number;
  comment: string;
  companyName: string;
}

interface ProblemReportPayload {
  description: string;
  urgency: 'low' | 'medium' | 'high';
  companyName: string;
}

@Component({
  selector: 'app-dashboard-help',
  templateUrl: './dashboard-help.component.html',
  styleUrls: ['./dashboard-help.component.scss'],
  providers: [FormatPipe]
})
export class DashboardHelpComponent implements OnInit {
  @Output() closed = new EventEmitter<void>();
  @Input() showAuthenticatedActions = true;
  @Input() sendProblemReportViaWhatsapp = false;
  @Input() showProblemReportAction = false;

  showHelpMenu = false;
  showFeedback = false;
  showProblemReport = false;
  showProblemReportConfirmation = false;
  helpMenuClosing = false;
  feedbackClosing = false;
  problemReportClosing = false;
  confirmationClosing = false;
  private confirmationTimeoutId: ReturnType<typeof setTimeout> | null = null;
  sendingFeedback = false;
  sendingProblemReport = false;
  rating = 0;
  feedbackComment = '';
  problemDescription = '';
  problemUrgency: ProblemReportPayload['urgency'] | '' = '';

  constructor(
    private notifier: MatSnackBar,
    private authService: AuthService,
    private dashboardService: DashboardService,
    private elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private format: FormatPipe
  ) { }

  ngOnInit(): void {
    this.renderer.appendChild(document.body, this.elementRef.nativeElement);
  }


  openHelp() {
    if (this.showProblemReportConfirmation) {
      this.closeProblemReportConfirmation();
      return;
    }

    this.helpMenuClosing = false;
    this.showHelpMenu = !this.showHelpMenu;
  }

  @HostListener('document:click', ['$event'])
  closeHelpOnOutsideClick(event: MouseEvent) {
    const target = event.target;

    if (
      !(target instanceof Element) ||
      target.closest(
        '.dashboard-floating-button, .dashboard-floating-button-container, .dashboard-feedback-container'
      )
    ) {
      return;
    }

    this.closeHelpMenu();
    this.closeFeedback();
    this.closeProblemReport();
  }

  @HostListener('document:keydown.escape')
  closeConfirmationOnEscape() {
    this.closeProblemReportConfirmation();
  }

  @HostListener('window:popstate')
  closeConfirmationOnBack() {
    this.closeProblemReportConfirmation();
  }

  whatsapp() {

    window.open(`https://wa.me/+${SUPPORT_WHATSAPP_NUMBER}`, '_blank');

    this.closeHelpMenu();
  }


  openFeedback() {
    if (this.showFeedback) {
      this.closeFeedback();
      return;
    }

    this.rating = 0;
    this.feedbackComment = '';
    this.feedbackClosing = false;
    this.showFeedback = true;
    this.closeHelpMenu();
  }

  openProblemReport() {
    this.problemDescription = '';
    this.problemUrgency = '';
    this.problemReportClosing = false;
    this.closeHelpMenu();
    this.showProblemReport = true;
  }

  closeProblemReport() {
    if (!this.showProblemReport || this.problemReportClosing || this.sendingProblemReport) {
      return;
    }

    this.problemReportClosing = true;
    setTimeout(() => {
      this.showProblemReport = false;
      this.problemReportClosing = false;
      this.problemDescription = '';
      this.problemUrgency = '';
    }, 180);
  }


  closeFeedback() {
    if (!this.showFeedback || this.feedbackClosing || this.sendingFeedback) {
      return;
    }

    this.feedbackClosing = true;
    setTimeout(() => {
      this.showFeedback = false;
      this.feedbackClosing = false;
      this.rating = 0;
      this.feedbackComment = '';
    }, 180);
  }

  private closeHelpMenu() {
    if (!this.showHelpMenu || this.helpMenuClosing) {
      return;
    }

    this.helpMenuClosing = true;
    setTimeout(() => {
      this.showHelpMenu = false;
      this.helpMenuClosing = false;
    }, 180);
  }

  selectRating(star: number) {
    this.rating = star;
  }

  getStarState(star: number): 'full' | 'empty' {
    if (this.rating >= star) {
      return 'full';
    }
    return 'empty';
  }

  submitFeedback() {

    if (this.rating < 1) {
      return;
    }

    const user = this.authService.getCurrentUser();
    const companyName = user?.tenant?.companyName?.trim();
    const companyCnpj = this.format.transform(user?.tenant?.companyCnpj, 'cnpj');
    const feedback: FeedbackPayload = {
      rating: this.rating,
      comment: this.feedbackComment.trim(),
      companyName: companyName && companyCnpj
        ? `${companyName.toLowerCase()} (${companyCnpj})`
        : companyName?.toLowerCase() || companyCnpj || ''
    };

    const title = companyName
      ? `Nova avaliação - ${companyName}`
      : companyCnpj
        ? `Nova avaliação - CNPJ: ${feedback.companyName}`
        : 'Nova avaliação';
    const text = [
      '               AVALIAÇÃO',
      '',
      `Empresa: ${feedback.companyName}`,
      `Nota: ${feedback.rating}/5`,
      'Comentário: ' + (feedback.comment || 'Nenhum comentário informado.'),
      '',
      '----------------------------------------',
      'Mensagem enviada pelo portal AntecipaGov.'
    ].join('\n');

    this.sendingFeedback = true;
    this.dashboardService.SendEmail(title, text).subscribe((response) => {
      this.sendingFeedback = false;
      this.closeHelpMenu();
      this.closeFeedback();
      this.confirmationClosing = false;
      this.showProblemReportConfirmation = true;
      this.confirmationTimeoutId = setTimeout(() => {
        this.closeProblemReportConfirmation();
      }, CONFIRMATION_TIMEOUT_MS);
    }, (error) => {
      this.sendingFeedback = false;
      console.error('Erro ao enviar feedback:', error);
      this.notifier.open('Não foi possível enviar o feedback. Tente novamente.', 'OK', {
        duration: 8000
      });
    });

  }

  submitProblemReport() {
    const description = this.problemDescription
      .trim()
      .replace(/^Relatar um Problema\s*:?\s*/i, '')
      .trim();
    if (!description || !this.problemUrgency) {
      return;
    }

    const user = this.authService.getCurrentUser();
    const companyName = user?.tenant?.companyName?.trim();
    const companyCnpj = this.format.transform(user?.tenant?.companyCnpj, 'cnpj');
    const report: ProblemReportPayload = {
      description,
      urgency: this.problemUrgency,
      companyName: companyName && companyCnpj
        ? `${companyName.toLowerCase()} (${companyCnpj})`
        : companyName?.toLowerCase() || companyCnpj || ''
    };

    const urgencyLabels: Record<ProblemReportPayload['urgency'], string> = {
      low: 'Baixa',
      medium: 'Média',
      high: 'Alta'
    };
    const title = companyName
      ? `Novo relato Problema - ${companyName}`
      : companyCnpj
        ? `Novo relato Problema - CNPJ: ${report.companyName}`
        : 'Novo relato Problema';
    const text = [
      '          RELATO DE PROBLEMA',
      '',
      report.companyName
        ? `Empresa: ${report.companyName}`
        : 'Suporte',
      `Urgência: ${urgencyLabels[report.urgency]}`,
      `Descrição: ${report.description}`,
      '',
      '----------------------------------------',
      'Mensagem enviada pelo portal AntecipaGov.'
    ].join('\n');

    if (this.sendProblemReportViaWhatsapp) {
      window.open(
        `https://wa.me/+${SUPPORT_WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`,
        '_blank'
      );
      this.closeProblemReport();
      this.closeHelpMenu();
      this.confirmationClosing = false;
      this.showProblemReportConfirmation = true;
      this.confirmationTimeoutId = setTimeout(() => {
        this.closeProblemReportConfirmation();
      }, CONFIRMATION_TIMEOUT_MS);
      return;
    }

    this.sendingProblemReport = true;
    this.dashboardService.SendEmail(title, text).subscribe((response) => {
      this.sendingProblemReport = false;
      this.closeProblemReport();
      this.closeHelpMenu();
      this.confirmationClosing = false;
      this.showProblemReportConfirmation = true;
      this.confirmationTimeoutId = setTimeout(() => {
        this.closeProblemReportConfirmation();
      }, CONFIRMATION_TIMEOUT_MS);
    }, (error) => {
      this.sendingProblemReport = false;
      console.error('Erro ao enviar relato de problema:', error);
      this.notifier.open('Não foi possível enviar o relato. Tente novamente.', 'OK', {
        duration: 8000
      });
    });

  }

  closeConfirmationOnFloatingButtonHover() {
    if (!this.showProblemReportConfirmation) {
      return;
    }

    if (this.confirmationTimeoutId !== null) {
      clearTimeout(this.confirmationTimeoutId);
      this.confirmationTimeoutId = null;
    }

    this.showProblemReportConfirmation = false;
    this.confirmationClosing = false;
    this.closed.emit();
  }

  closeProblemReportConfirmation() {
    if (this.confirmationClosing || !this.showProblemReportConfirmation) {
      return;
    }

    this.confirmationClosing = true;
    this.closed.emit();
    if (this.confirmationTimeoutId !== null) {
      clearTimeout(this.confirmationTimeoutId);
      this.confirmationTimeoutId = null;
    }
    setTimeout(() => {
      this.showProblemReportConfirmation = false;
      this.confirmationClosing = false;
    }, 180);
  }

}
